// React imports
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// App imports
import { ScrollableContent } from 'src/components/ScrollableContent';
import { IProjectDetails, IProjectResponse } from 'src/models/Project';
import { TableLoader } from 'src/views/TimeSheet/components/TableLoader';
import { IActivity } from 'src/models/Activity';

import {
  assignActivityToProject,
  fetchProjectsById,
  getProjectById,
} from 'src/redux/features/projects/projectsSlice';
import { CopyActivityModal } from '../CopyActivityModal';
import ProjectsAutoComplete from 'src/components/ProjectsAutoComplete';
import {
  getActivities,
  getAssignedProjects,
} from 'src/redux/features/session/slice';
import { toast } from 'react-toastify';
import { AssignedProjectsAutoComplete } from 'src/components/AssignedProjectsAutoComplete';
import { CanView } from 'src/components/CanView';
import { getUserRoles } from 'src/redux/features/users/usersSlice';
import { isAuthorised } from 'src/utils/helpers';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import { Flex, Input, Switch, Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { setError } from 'src/redux/features/common/commonSlice';

const ProjectActivities = ({
  isModalOpen,
  selectedProject,
  setSelectedProject,
}) => {
  const dispatch = useDispatch();
  const addActivityBtnRef = useRef(null);

  const { data: activities, loading: activitiesLoading } =
    useSelector(getActivities);

  const userRoles = useSelector(getUserRoles);

  const { data: projects, loading: projectsLoading } =
    useSelector(getAssignedProjects);

  const {
    data: activityDetails,
    loading: activitiesDetailsLoading,
  }: {
    data: IProjectDetails;
    loading: boolean;
  } = useSelector(getProjectById);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<IActivity[]>(activities);
  const [copyActivity, setCopyActivity] = useState<IActivity[]>(activities);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [category, setCategory] = useState<string | null>(null);

  const addRow = () => {
    const newRow: IActivity = {
      id: data.length + 1,
      name: '',
      isNew: true,
      isDefault: false,
      isCapitalizable: true,
      isActive: true,
    };
    const updatedDate = [...data];

    updatedDate.unshift(newRow);
    setData(updatedDate);
  };

  const onAvtivityTextChange = (e, index) => {
    const activityName = e.target.value;
    const newData = [...data];

    newData[index].name = activityName;

    setData([...newData]);
  };

  const removeRow = (index) => {
    const newData = [...data];

    newData.splice(index, 1);
    setData(newData);
  };

  const handleClose = () => {
    setShowCopyModal(false);
  };

  const getActivitiesByProjectId = async (id: number) => {
    setIsLoading(true);
    try {
      const result = await dispatch(fetchProjectsById({ id }));

      setCopyActivity(
        result.payload.activityTypes.filter((activity) => !activity.isDefault),
      );

      setCategory(result.payload.category);
    } finally {
      setIsLoading(false);
    }
  };

  const onStatusChange = (index) => {
    if (
      selectedProject?.name?.toLowerCase() === en.Activities.Label.TimeOff &&
      data[index].isActive &&
      data.filter((x) => x.isActive).length === 1
    )
      return dispatch(setError(en.Activities.Error.AtleastOneActivity));
    data[index].isActive = !data[index].isActive;
    setData([...data]);
  };

  const onCapitalizableChange = (index) => {
    data[index].isCapitalizable = !data[index].isCapitalizable;
    setData([...data]);
  };

  const onCopy = () => {
    setShowCopyModal(true);
  };

  const onSelectedProjectChange = (selectedItem: IProjectResponse) => {
    if (selectedItem) {
      setSelectedProject(selectedItem);
      getActivitiesByProjectId(selectedItem.id);
    } else {
      setSelectedProject(null);
    }
  };

  function identifyDuplicateNames(data) {
    const nameSet = new Set();
    const duplicates = [];

    for (const item of data) {
      const name = item.name;

      if (nameSet.has(name)) {
        duplicates.push(name);
      } else {
        nameSet.add(name);
      }
    }

    return duplicates;
  }

  const columns = [
    {
      title: en.Activities.Title.activityName,
      dataIndex: 'name',
      key: 'name',
      render: (text, record, index) => (
        <Input
          value={text}
          placeholder={en.Activities.Placeholder.enterActivityName}
          disabled={record.isDefault}
          onChange={(e) => onAvtivityTextChange(e, index)}
        />
      ),
      width: '40%',
    },
    {
      title: en.Activities.Title.capitalizable,
      dataIndex: 'isCapitalizable',
      key: 'isCapitalizable',
      render: (value, record, index) => (
        <Switch
          checked={value}
          disabled={record.isDefault}
          onChange={() => onCapitalizableChange(index)}
          size="small"
        />
      ),
    },
    {
      title: en.Common.Title.status,
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value, record, index) => (
        <Switch
          checked={value}
          disabled={record.isDefault}
          onChange={() => onStatusChange(index)}
          size="small"
        />
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record, index) =>
        record?.isNew ? (
          <AntDButton
            type="link"
            prefixIcon={<DeleteOutlined />}
            onClick={() => removeRow(index)}
            htmlType="button"
          />
        ) : null,
    },
  ];

  const onSave = async () => {
    const isActivitiesValid = data.every((item) => item.name !== '');

    if (!isActivitiesValid) {
      dispatch(setError(en.Activities.Error.enterActivityName));

      return;
    }
    const duplicateActivities = identifyDuplicateNames(data);

    if (duplicateActivities && duplicateActivities.length > 0) {
      dispatch(setError(en.Activities.Error.activityNameUnique));

      return;
    }

    setIsSaving(true);

    const response = await dispatch(
      assignActivityToProject({
        id: selectedProject?.id,
        payload: {
          activities: data
            .filter((it) => !it.isDefault)
            .map((it) => {
              return {
                ...it,
                isDefault: false,
                id: it.isNew ? 0 : it.id,
              };
            }),
        },
      }),
    );

    setIsSaving(false);
    if (response.error) {
      return;
    }

    toast.success(en.Activities.Success.activitySaved);
    getActivitiesByProjectId(selectedProject.id);
  };

  useEffect(() => {
    if (selectedProject?.id && !showCopyModal) {
      if (category === 'Product Initiatives') {
        setData(activityDetails.activityTypes as IActivity[]);
      } else {
        setData(
          activityDetails.activityTypes.filter(
            (activity) => !activity.isDefault,
          ) as IActivity[],
        );
      }
    }
  }, [selectedProject?.id, activityDetails, category]);

  useEffect(() => {
    setData(activities.filter((act) => !act.isDefault));
  }, [activities]);

  return (
    <>
      <Flex
        gap={'middle'}
        justify="space-between"
        style={{ paddingBottom: '1rem' }}
      >
        <Flex>
          {isAuthorised(userRoles.data, ['CapexAdmin']) ? (
            <ProjectsAutoComplete
              onSelectedProjectChange={onSelectedProjectChange}
              style={{ width: '400px' }}
              selectedTargetProjectId={selectedProject?.id}
            />
          ) : (
            <AssignedProjectsAutoComplete
              style={{ width: '400px' }}
              onSelectedProjectChange={onSelectedProjectChange}
              selectedTargetProjectId={selectedProject?.id}
            />
          )}
        </Flex>
        {!activitiesLoading && selectedProject?.id && (
          <Flex gap={'middle'}>
            <AntDButton
              type="primary"
              onClick={addRow}
              statusOnComplete="Add Activity"
              htmlType="button"
            />
            <AntDButton
              type="default"
              onClick={onCopy}
              disabled={!copyActivity?.find((status) => !status.isDefault)}
              statusOnComplete="Copy"
              htmlType="button"
            />
          </Flex>
        )}
      </Flex>

      <ScrollableContent
        minHeight={
          selectedProject?.id
            ? 'calc(100vh - 18.75rem)'
            : 'calc(100vh - 15.75rem)'
        }
        maxHeight={
          selectedProject?.id
            ? 'calc(100vh - 18.75rem)'
            : 'calc(100vh - 15.75rem)'
        }
      >
        <Table
          dataSource={selectedProject?.id ? data : []}
          columns={columns}
          rowKey={(record, index) => `${record.id}-${index}`}
          pagination={false}
          sticky
          loading={isLoading}
        />
      </ScrollableContent>
      <CopyActivityModal
        showCopyModal={showCopyModal}
        handleClose={handleClose}
        isModalOpen={showCopyModal}
        selectedProject={selectedProject?.name}
        selectedActivity={copyActivity}
      />

      <Flex justify="flex-end" style={{ paddingTop: '1rem' }}>
        {selectedProject && selectedProject.id ? (
          <AntDButton
            onClick={onSave}
            type="primary"
            htmlType="button"
            status="Saving..."
            loading={isSaving}
            statusOnComplete="Save"
          />
        ) : (
          ''
        )}
      </Flex>
    </>
  );
};

export default ProjectActivities;
